/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useEffect, useState } from 'react';
import { format, isBefore, startOfDay } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { DateChangeEvent } from 'carbon-react/lib/components/date';

import { BusinessInsightSchedule } from '@futrli-sdk/predict';
import { Logger } from '@futrli/shared-services';
import { OrganisationOverviewView } from '@futrli/views/OrganisationOverviewView';
import { toastError, toastSuccess } from '@futrli/ui-components';

import { DayNumber, forceLocalDate, getFollowingDayDate } from '../../../utils/dates';
import { Option, PeriodOption } from '../../types';
import { Recurrence, RecurrenceEndType, RecurrenceWeekDay, THE_LAST } from './types';
import { TRACKING_EVENTS } from '../../../core/constants';
import { trackEvent } from '../../../core/tracker';

interface UseEmailInsightsFormParams {
  activeSchedule?: BusinessInsightSchedule;
  selectedForecastId?: string;
  period: Option;
  view: OrganisationOverviewView;
  handleClose: () => void;
}

export const useEmailInsightsForm = ({
  activeSchedule,
  selectedForecastId,
  period,
  view,
  handleClose
}: UseEmailInsightsFormParams) => {
  const initialRecurrenceStartDate = getFollowingDayDate(DayNumber.MONDAY);

  const [isLoading, setIsLoading] = useState(false);
  const [recurrence, setRecurrence] = useState(Recurrence.WEEKLY);
  const [recurrenceStartDate, setRecurrenceStartDate] = useState(initialRecurrenceStartDate);
  const [recurrenceStartDateError, setRecurrenceStartDateError] = useState(false);
  const [recurrenceRepeatValue, setRecurrenceRepeatValue] = useState('1');
  const [recurrenceEndType, setRecurrenceEndType] = useState(RecurrenceEndType.NEVER);
  const [occurancesValue, setOccurancesValue] = useState('10');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(initialRecurrenceStartDate);
  const [recurrenceMonthDay, setRecurrenceMonthDay] = useState(THE_LAST);
  const [recurrenceWeekDay, setRecurrenceWeekDay] = useState<RecurrenceWeekDay>(RecurrenceWeekDay.MONDAY);

  useEffect(() => {
    if (!activeSchedule) {
      return;
    }
    setRecurrence(activeSchedule.period as Recurrence);
    setRecurrenceStartDate(forceLocalDate(activeSchedule.start_date));
    setRecurrenceRepeatValue(String(activeSchedule.repeating_frequency));
    setRecurrenceEndType(activeSchedule.end_type as RecurrenceEndType);
    setOccurancesValue(
      typeof activeSchedule.end_after_occurrences === 'number' ? String(activeSchedule.end_after_occurrences) : '10'
    );
    setRecurrenceEndDate(forceLocalDate(activeSchedule.end_date || ''));
    setRecurrenceWeekDay(
      activeSchedule.weekday ? (activeSchedule.weekday as RecurrenceWeekDay) : RecurrenceWeekDay.MONDAY
    );
    setRecurrenceMonthDay(
      activeSchedule.day_of_month
        ? activeSchedule.day_of_month === 31
          ? THE_LAST
          : String(activeSchedule.day_of_month)
        : THE_LAST
    );
    const isStartRecurrenceInPast = isBefore(
      startOfDay(forceLocalDate(activeSchedule.start_date)),
      startOfDay(new Date())
    );
    setRecurrenceStartDateError(isStartRecurrenceInPast);
  }, [activeSchedule]);

  const handleRecurrenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRecurrence(event.target.value as Recurrence);
  };

  const handleRecurrenceStartDateChange = (e: DateChangeEvent) => {
    setRecurrenceStartDate(forceLocalDate(e.target.value.rawValue || ''));
    // If we have start date error, we need to choose start date in future to dismiss this error,
    // only future dates will be allowed for user to choose in this case
    if (recurrenceStartDateError) {
      setRecurrenceStartDateError(false);
    }
  };

  const handleRecurrenceValueChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setRecurrenceRepeatValue(event.target.value);

  const handleRecurrenceEndTypeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setRecurrenceEndType(event.target.value as RecurrenceEndType);

  const handleOccurancesValueChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setOccurancesValue(event.target.value);

  const handleRecurrenceEndDateChange = (e: DateChangeEvent) =>
    setRecurrenceEndDate(forceLocalDate(e.target.value.rawValue || ''));

  const handleRecurrenceWeekDayChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setRecurrenceWeekDay(event.target.value as RecurrenceWeekDay);

  const handleRecurrenceMonthDay = (event: React.ChangeEvent<HTMLInputElement>) =>
    setRecurrenceMonthDay(event.target.value);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      if (recurrence === Recurrence.OPT_OUT_THIS && activeSchedule) {
        await view.deleteSchedule(activeSchedule.id);
        toastSuccess(
          <FormattedMessage id="report.schedule_has_been_deleted" defaultMessage="Schedule has been deleted" />
        );
      } else if (recurrence === Recurrence.OPT_OUT_ALL) {
        await view.deleteAllSchedules();
        toastSuccess(
          <FormattedMessage id="report.all_schedules_has_been_saved" defaultMessage="All schedules have been deleted" />
        );
      } else {
        const sharedPayloadData = {
          forecast_id: selectedForecastId || null,
          period: recurrence as string,
          start_date: format(recurrenceStartDate, 'yyyy-MM-dd'),
          end_type: recurrenceEndType,
          end_date:
            recurrenceEndType === RecurrenceEndType.ON_THIS_DAY ? format(recurrenceEndDate, 'yyyy-MM-dd') : null,
          end_after_occurrences:
            recurrenceEndType === RecurrenceEndType.AFTER_N_OCCURANCES ? Number(occurancesValue) : null,
          repeating_frequency: Number(recurrenceRepeatValue),
          insight_period:
            period.value === PeriodOption.THIS_MONTH
              ? Recurrence.MONTHLY
              : period.value === PeriodOption.THIS_QUARTER
              ? Recurrence.QUARTERLY
              : Recurrence.ANNUALLY
        };

        let payload = {};
        if (recurrence === Recurrence.DAILY) {
          payload = {
            ...sharedPayloadData,
            day_of_month: null,
            weekday: null
          };
        } else if (recurrence === Recurrence.MONTHLY) {
          payload = {
            ...sharedPayloadData,
            day_of_month: recurrenceMonthDay === THE_LAST ? 31 : Number(recurrenceMonthDay),
            weekday: null
          };
        } else {
          payload = {
            ...sharedPayloadData,
            day_of_month: null,
            weekday: recurrenceWeekDay
          };
        }

        if (activeSchedule) {
          await view.updateSchedule(activeSchedule.id, payload);
        } else {
          await view.createSchedule(payload);
        }

        trackEvent(TRACKING_EVENTS.SET_UP_INSIGHTS_EMAIL_HOME_PAGE);
        toastSuccess(
          <FormattedMessage id="reporting.schedule_has_been_saved" defaultMessage="Schedule has been saved" />
        );
      }
      handleClose();
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const recurrenceEndDateIsBeforeStartDate =
    recurrenceEndType === RecurrenceEndType.ON_THIS_DAY &&
    isBefore(startOfDay(recurrenceEndDate), startOfDay(recurrenceStartDate));

  const recurrenceRepeatValueError =
    !recurrenceRepeatValue || Number(recurrenceRepeatValue) < 1;

  const showRecurrenceSettings =
    recurrence === Recurrence.DAILY || recurrence === Recurrence.MONTHLY || recurrence === Recurrence.WEEKLY;

  return {
    isLoading,
    recurrence,
    recurrenceStartDate,
    recurrenceStartDateError,
    recurrenceRepeatValue,
    recurrenceRepeatValueError,
    recurrenceEndType,
    occurancesValue,
    recurrenceEndDate,
    recurrenceMonthDay,
    recurrenceWeekDay,
    recurrenceEndDateIsBeforeStartDate,
    showRecurrenceSettings,
    handleRecurrenceChange,
    handleRecurrenceStartDateChange,
    handleRecurrenceValueChange,
    handleRecurrenceEndTypeChange,
    handleOccurancesValueChange,
    handleRecurrenceEndDateChange,
    handleRecurrenceWeekDayChange,
    handleRecurrenceMonthDay,
    handleSave
  };
};
